import React, { useContext, useState, useEffect } from 'react';
import { IcheerContext } from '../Context/AppContext';
import {
  LogOut, User, ChevronLeft, ChevronRight, Package, DollarSign,
  ShoppingBag, Info, Search, StickyNote, Tag, TrendingUp,
  Edit3, Save, X, AlertCircle, CheckCircle, Users, Building, UserCheck
} from 'lucide-react';
import Logo from '../components/Logo';

// ===========================================
// TEAM INFO MODAL (STABLE, OUTSIDE MAIN)
// ===========================================
const TeamModal = ({ show, onClose, team, labels, getTeamTypeDescription }) => {
  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content border-0" style={{ borderRadius: '24px', overflow: 'hidden' }}>
          <div className="modal-header border-0 pb-0" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="w-100 text-center text-white py-3">
              <div className="bg-white bg-opacity-20 rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{ width: '80px', height: '80px' }}>
                <ShoppingBag size={40} className="text-white" />
              </div>
              <h4 className="mb-1 fw-bold">{team.Name}</h4>
              <p className="mb-0 opacity-90">{team.first_name} {team.last_name}</p>
            </div>
            <button type="button" className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
              onClick={onClose}></button>
          </div>
          <div className="modal-body p-4">
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card h-100 border-0 bg-light">
                  <div className="card-body p-4">
                    <h6 className="text-muted mb-3">{labels.detailsHeader}</h6>
                    <div className="space-y-3">
                      {team?.team_type && team.team_type !== 'N/A' && (
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <span className="text-muted">Type</span>
                          <div className="text-end">
                            <span className={`badge ${team.team_type === 'Premium' ? 'bg-warning' : 'bg-info'} px-3 py-2 mb-2`}>
                              {team.team_type}
                            </span>
                            <div className="small text-muted" style={{ maxWidth: '200px' }}>
                              {getTeamTypeDescription()}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-muted">Status</span>
                        <span className={`badge ${team.status === 'verified' ? 'bg-success' : 'bg-secondary'} px-3 py-2`}>
                          {team.status}
                        </span>
                      </div>
                      {team.team_size && team.team_size !== 'N/A' && (
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="text-muted">Size</span>
                          <span className="badge bg-light text-dark px-3 py-2">
                            {team.team_size}
                          </span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-muted">Registration</span>
                        <span className="badge bg-primary px-3 py-2">
                          {team.brand_ambassador_code ? (
                            team.brand_ambassador_code
                          ) : (
                            (team.registration_type || 'team').charAt(0).toUpperCase() + (team.registration_type || 'team').slice(1)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card h-100 border-0 bg-light">
                  <div className="card-body p-4">
                    <h6 className="text-muted mb-3">Referral Code</h6>
                    <div className="text-center">
                      <div className="bg-primary text-white px-4 py-3 rounded-3 mb-3"
                        style={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '1px' }}>
                        {team.Promocode}
                      </div>
                      <small className="text-muted">Share this referral code with customers</small>
                      {team.old_referral_codes && team.old_referral_codes.length > 0 && (
                        <div className="mt-3">
                          <small className="text-muted d-block mb-1">Previous referral Codes:</small>
                          <div className="d-flex flex-wrap gap-1 justify-content-center">
                            {team.old_referral_codes.map((code, i) => (
                              <span key={i} className="badge bg-secondary fs-6">{code}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="card border-0 bg-light">
                  <div className="card-body p-4">
                    <h6 className="text-muted mb-3">Contact Information</h6>
                    <div className="mb-3">
                      <label className="form-label small text-muted">Email Address</label>
                      <div className="fw-semibold">{team.email}</div>
                    </div>
                    <div>
                      <label className="form-label small text-muted">Address</label>
                      <p className="mb-0 text-dark">{team.street_address}</p>
                      {(team.city || team.state || team.zip_code) && (
                        <p className="mb-0 text-dark">
                          {team.city}{team.city && (team.state || team.zip_code) && ', '}
                          {team.state} {team.zip_code}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// EDIT PROFILE MODAL (STABLE, OUTSIDE MAIN)
// ===========================================
const EditProfileModal = ({
  show,
  onClose,
  editForm,
  setEditForm,
  editLoading,
  editMessage,
  editMessageType,
  setEditMessage,
  handleSaveProfile,
  handleRegistrationTypeChange,
  getNameLabel,
  teamTypeOptions,
  sizeOptions,
  registrationTypes
}) => {
  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content border-0" style={{ borderRadius: '24px', overflow: 'hidden' }}>
          <div className="modal-header border-0" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <h5 className="modal-title text-white fw-bold">
              <Edit3 size={20} className="me-2" />
              Edit Profile
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body p-4">
            {editMessage && (
              <div className={`alert alert-${editMessageType} alert-dismissible border-0 shadow-sm mb-3`}>
                <div className="d-flex align-items-center">
                  {editMessageType === 'success' ? <CheckCircle size={16} className="me-2" /> : <AlertCircle size={16} className="me-2" />}
                  <div>{editMessage}</div>
                  <button className="btn-close" onClick={() => setEditMessage('')}></button>
                </div>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-semibold">Registration Type</label>
              <select
                className="form-select"
                value={editForm.registration_type}
                onChange={handleRegistrationTypeChange}
              >
                {registrationTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">First Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Last Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">{getNameLabel()}</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.Name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, Name: e.target.value }))}
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Brand Ambassador Code (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.brand_ambassador_code || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, brand_ambassador_code: e.target.value }))}
                  placeholder="Enter code if applicable"
                />
                <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Did one of our Brand Ambassador Captains help you sign up?
                </small>
              </div>


              <div className="col-12">
                <label className="form-label fw-semibold">Referral Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.Promocode}
                  onChange={(e) => setEditForm(prev => ({
                    ...prev,
                    Promocode: e.target.value.replace(/\s/g, '')
                  }))}
                  minLength={6}
                />
                <small className="text-muted">Min 6 characters, no spaces</small>
              </div>

              {editForm.registration_type === 'team' && (
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Team Type</label>
                  <select
                    className="form-select"
                    value={editForm.team_type}
                    onChange={(e) => setEditForm(prev => ({ ...prev, team_type: e.target.value }))}
                  >
                    <option value="">Select team type</option>
                    {teamTypeOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}

              {(editForm.registration_type === 'team' || editForm.registration_type === 'gym') && (
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {editForm.registration_type === 'team' ? 'Team Size' : 'Gym Size'}
                  </label>
                  <select
                    className="form-select"
                    value={editForm.team_size}
                    onChange={(e) => setEditForm(prev => ({ ...prev, team_size: e.target.value }))}
                  >
                    <option value="">Select size</option>
                    {sizeOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-12">
                <label className="form-label fw-semibold">Street Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.street_address || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, street_address: e.target.value }))}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">City</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.city || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">State</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.state || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">Zip Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.zip_code || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer border-0">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={editLoading}>
              <X size={16} className="me-1" /> Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveProfile} disabled={editLoading}>
              {editLoading ? <>Saving...</> : <><Save size={16} className="me-1" /> Save Changes</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// MAIN HOMEPAGE COMPONENT
// ===========================================
const HomePage = () => {
  const { host, team, setIsAdminLoggedIn, fetchTeamInfo, websiteDetails } = useContext(IcheerContext);
  const userEmail = localStorage.getItem('userEmail') || '';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [editForm, setEditForm] = useState({
    Name: '', first_name: '', last_name: '', Promocode: '',
    registration_type: '', street_address: '', city: '', state: '', zip_code: '',
    brand_ambassador_code: '', team_type: '', team_size: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [editMessageType, setEditMessageType] = useState('');

  const teamTypeOptions = [
    'Elementary', 'Middle', 'High School', 'College UCA', 'College NCA', 'All Star', 'Recreational'
  ];
  const sizeOptions = ['0–50 Cheerleaders', '50–100 Cheerleaders', '100+ Cheerleaders'];
  const registrationTypes = [
    { value: 'team', label: 'Team', icon: Users },
    { value: 'gym', label: 'Gym', icon: Building },
    { value: 'individual', label: 'Individual Cheerleader', icon: UserCheck }
  ];

  // Initialize edit form
  useEffect(() => {
    fetchTeamInfo()
  }, []);

  // Initialize edit form
  useEffect(() => {
    if (showEditModal && team) {
      setEditForm({
        Name: team.Name || '',
        first_name: team.first_name || '',
        last_name: team.last_name || '',
        Promocode: team.Promocode || '',
        registration_type: team.registration_type || 'team',
        street_address: team.street_address || '',
        city: team.city || '',
        state: team.state || '',
        zip_code: team.zip_code || '',
        brand_ambassador_code: team.brand_ambassador_code || '',
        team_type: team.team_type || '',
        team_size: team.team_size || ''
      });
      setEditMessage('');
    }
  }, [showEditModal, team]);

  // Dynamic labels
  const getRegistrationLabels = () => {
    const t = team?.registration_type || 'team';
    return t === 'gym'
      ? { infoButton: 'Gym Info', detailsHeader: 'Gym Details' }
      : t === 'individual'
        ? { infoButton: 'Cheerleader Info', detailsHeader: 'Cheerleader Details' }
        : { infoButton: 'Team Info', detailsHeader: 'Team Details' };
  };

  const getTeamTypeDescription = () => {
    const desc = {
      'Elementary': 'Ages 5-11 • Youth cheerleading program',
      'Middle': 'Ages 11-14 • Junior competitive level',
      'High School': 'Ages 14-18 • School-based cheerleading',
      'College UCA': 'University level • UCA competition division',
      'College NCA': 'University level • NCA competition division',
      'All Star': 'Club-based • Competitive all-star program',
      'Recreational': 'Community-based • Non-competitive program'
    };
    return desc[team?.team_type] || team?.team_type || 'General';
  };

  const getNameLabel = () => {
    const t = editForm.registration_type;
    return t === 'team' ? 'Team Name' : t === 'gym' ? 'Gym Name' : t === 'individual' ? 'Gym / Team Name' : 'Name';
  };

  const handleRegistrationTypeChange = (e) => {
    const newType = e.target.value;
    setEditForm(prev => ({
      ...prev,
      registration_type: newType,
      team_type: newType === 'team' ? prev.team_type : '',
      team_size: (newType === 'team' || newType === 'gym') ? prev.team_size : '',
    }));
  };

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!team.Promocode && (!team.old_referral_codes || team.old_referral_codes.length === 0)) return;
      setLoading(true);
      try {
        const res = await fetch(`${host}order/discounted/simplified`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            discount_code: team.Promocode,
            old_referral_codes: team.old_referral_codes || []
          }),
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
        const data = await res.json();
        setOrders(data.orders || []);
        setError('');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [host, team]);

  const handleSaveProfile = async () => {
    setEditLoading(true);
    setEditMessage('');
    setEditMessageType('');

    try {
      // === STEP 1: VALIDATION BASED ON REGISTRATION TYPE ===
      const errors = [];

      // Always required
      if (!editForm.first_name?.trim()) errors.push('First name is required.');
      if (!editForm.last_name?.trim()) errors.push('Last name is required.');
      if (!editForm.Promocode?.trim()) errors.push('Referral code is required.');
      if (editForm.Promocode && editForm.Promocode.length < 6)
        errors.push('Referral code must be at least 6 characters.');

      // Conditional required fields
      const type = editForm.registration_type;

      if (type === 'team') {
        if (!editForm.Name?.trim()) errors.push('Team Name is required.');
        if (!editForm.team_type?.trim()) errors.push('Team Type is required.');
        if (!editForm.team_size?.trim()) errors.push('Team Size is required.');
      }
      else if (type === 'gym') {
        if (!editForm.Name?.trim()) errors.push('Gym Name is required.');
        if (!editForm.team_size?.trim()) errors.push('Gym Size is required.');
      }
      else if (type === 'individual') {
        if (!editForm.Name?.trim()) errors.push('Gym / Team Name is required.');
      }

      // Address fields required
      if (!editForm.street_address?.trim()) errors.push('Street address is required.');
      if (!editForm.city?.trim()) errors.push('City is required.');
      if (!editForm.state?.trim()) errors.push('State is required.');
      if (!editForm.zip_code?.trim()) errors.push('Zip code is required.');

      // If any errors, show them
      if (errors.length > 0) {
        setEditMessage(errors.join(' '));
        setEditMessageType('danger');
        setEditLoading(false);
        return;
      }

      // === STEP 2: API CALL ===
      const token = localStorage.getItem('authToken') || userEmail;
      const res = await fetch(`${host}team/profile/edit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: userEmail, ...editForm })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update profile');
      }

      // Success
      setEditMessage('Profile updated successfully!');
      setEditMessageType('success');
      await fetchTeamInfo();
      setTimeout(() => setShowEditModal(false), 1500);

    } catch (err) {
      setEditMessage(err.message);
      setEditMessageType('danger');
    } finally {
      setEditLoading(false);
    }
  };

  // Export CSV
  const exportToCSV = () => {
    const allKeys = new Set();
    orders.forEach(order => {
      order.lineItems?.edges?.forEach(edge => {
        edge.node.customAttributes?.forEach(attr => allKeys.add(attr.key));
      });
    });
    const customAttrKeys = Array.from(allKeys);
    const headers = ['Order ID', 'Product Title', 'Quantity', 'Price', ...customAttrKeys, 'Order Date', 'Total Amount (Order)'];
    const rows = orders.flatMap(order =>
      order.lineItems?.edges?.map(edge => {
        const item = edge.node;
        const attrMap = {};
        item.customAttributes?.forEach(attr => { attrMap[attr.key] = attr.value; });
        return [
          order.name,
          item.title,
          item.quantity,
          parseFloat(item.originalTotalSet?.shopMoney?.amount || 0).toFixed(2),
          ...customAttrKeys.map(key => attrMap[key] || ''),
          new Date(order.createdAt).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
          parseFloat(order.totalPriceSet?.shopMoney?.amount || 0).toFixed(2),
        ];
      }) || []
    );
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdminLoggedIn');
    localStorage.removeItem('userEmail');
    setIsAdminLoggedIn(false);
  };

  // Filter & stats
  const filteredOrders = orders.filter(o =>
    o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.lineItems?.edges?.some(e => e.node.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalRaised = filteredOrders.reduce((sum, o) => {
    return sum + (o.lineItems?.edges?.reduce((isum, e) => {
      const qty = e.node.quantity;
      const hasPack = (e.node.product?.tags || []).includes('pack');
      const disc = team.apply_team_funds
        ? hasPack ? team.discount_quantity_variety_pack : team.discount_quantity
        : hasPack ? websiteDetails.discount_quantity_variety_pack : websiteDetails.discount_quantity;
      return isum + qty * disc;
    }, 0) || 0);
  }, 0);

  const totalCasesSold = filteredOrders.reduce((sum, o) =>
    sum + (o.lineItems?.edges?.reduce((isum, e) => {
      const qty = e.node.quantity;
      const hasPack = (e.node.product?.tags || []).some(t => t.toLowerCase() === 'pack');
      return isum + (hasPack ? qty * 4 : qty);
    }, 0) || 0), 0
  );

  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const labels = getRegistrationLabels();

  return (
    <div style={{ backgroundColor: '#f6dce2', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ backgroundColor: "#dc59a0" }} className="shadow-sm border-bottom">
        <div className="container px-4">
          <div className="row align-items-center py-4">
            <div className="col-md-5">
              <div className="d-flex align-items-center">
                <Logo />
                <div className='px-0 px-md-3'>
                  <h3 className="mb-1 fw-bold text-white">iCheer Dashboard</h3>
                  <p className="text-white mb-0 small">Welcome back, {team?.Name}</p>
                </div>
              </div>
            </div>
            <div className="col-md-7 text-end">
              <div className="d-flex justify-content-end gap-2">
                <button onClick={() => setShowEditModal(true)} className="btn btn-outline-primary rounded-pill px-4 custom-btn">
                  <Edit3 size={16} className="me-1" /> Edit Profile
                </button>
                <button onClick={() => setShowTeamModal(true)} className="btn btn-outline-primary rounded-pill px-4 custom-btn">
                  <Info size={16} className="me-1" /> {labels.infoButton}
                </button>
                <button onClick={exportToCSV} className="btn btn-outline-primary rounded-pill px-4 custom-btn" disabled={orders.length === 0}>
                  <StickyNote size={16} className="me-1" /> Export Orders
                </button>
                <button onClick={handleLogout} className="btn btn-primary rounded-pill px-4">
                  <LogOut size={16} className="me-1" /> Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container px-4 py-4">
        {/* Stats */}
        <div className="row g-4 mb-5">
          <div className="col-xl-4 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-muted mb-1 small">Total Orders</p>
                    <h2 className="fw-bold mb-0 text-dark">{filteredOrders.length}</h2>
                    <small className="text-success"><TrendingUp size={12} className="me-1" /> Active orders</small>
                  </div>
                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                    <Package className="text-primary" size={28} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-4 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-muted mb-1 small">Total Raised</p>
                    <h2 className="fw-bold mb-0 text-dark">${totalRaised.toFixed(2)}</h2>
                    <small className="text-success"><DollarSign size={12} className="me-1" /> USD currency</small>
                  </div>
                  <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                    <DollarSign className="text-success" size={28} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-4 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-muted mb-1 small">Boxes Sold</p>
                    <h2 className="fw-bold mb-0 text-dark">{totalCasesSold}</h2>
                    <small className="text-info"><ShoppingBag size={12} className="me-1" /> Total items</small>
                  </div>
                  <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px' }}>
                    <ShoppingBag className="text-info" size={28} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-danger border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">{error}</div>
              <button type="button" class Name="btn-close" onClick={() => setError('')}></button>
            </div>
          </div>
        )}

        {/* Orders Table */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <div className="card-header bg-white border-0 py-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div>
                <h4 className="mb-1 fw-bold">Recent Orders</h4>
                <p className="text-muted mb-0 small">Orders placed using your referral code</p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <div className="position-relative">
                  <Search size={16} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                  <input
                    type="text"
                    className="form-control ps-5 rounded-pill"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ minWidth: '200px' }}
                  />
                </div>
                <span className="badge bg-primary fs-6 px-3 py-2 rounded-pill">
                  {filteredOrders.length} orders
                </span>
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h6 className="text-muted">Loading your orders...</h6>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-5">
                <Package size={64} className="text-muted mb-3" />
                <h5 className="text-muted mb-2">
                  {searchTerm ? 'No matching orders found' : 'No orders found'}
                </h5>
                <p className="text-muted">
                  {searchTerm ? 'Try adjusting your search terms' : 'Orders using your referral code will appear here'}
                </p>
                {searchTerm && (
                  <button className="btn btn-outline-primary btn-sm" onClick={() => setSearchTerm('')}>
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="row g-4">
                  {currentOrders.map(order => {
                    const discountApp = order.discountApplications?.edges?.[0]?.node;
                    const originalAmount = parseFloat(order.totalPriceSet?.shopMoney?.amount || 0);
                    const discountPercentage = discountApp?.value?.percentage || 0;
                    const discountAmount = originalAmount * discountPercentage / (100 - discountPercentage);

                    return (
                      <div key={order.id} className="col-12">
                        <div className="card border shadow-sm" style={{ borderRadius: '16px' }}>
                          <div className="card-header bg-gradient border-0" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', borderRadius: '16px 16px 0 0' }}>
                            <div className="row align-items-center">
                              <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                  <div className="bg-primary rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                    <Tag size={20} className="text-white" />
                                  </div>
                                  <div>
                                    <h6 className="mb-1 fw-bold">Order {order.name}</h6>
                                    <small className="text-muted">
                                      {new Date(order.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} •{' '}
                                      {new Date(order.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6 text-md-end mt-3 mt-md-0">
                                <div className="d-flex flex-column align-items-md-end">
                                  <h4 className="mb-1 fw-bold text-success">${originalAmount.toFixed(2)}</h4>
                                  {discountPercentage > 0 && (
                                    <span className="badge bg-success rounded-pill px-3">
                                      {discountPercentage}% OFF • ${discountAmount.toFixed(2)} saved
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h6 className="mb-0 fw-semibold">Total Boxes</h6>
                              <span className="badge bg-light text-dark">
                                {order.lineItems?.edges?.reduce((sum, edge) => {
                                  const quantity = edge.node.quantity;
                                  const productTags = edge.node.product?.tags || [];
                                  const hasPackTag = productTags.some(tag => tag.toLowerCase() === "pack");
                                  return sum + (hasPackTag ? quantity * 4 : quantity);
                                }, 0) || 0} {order.lineItems?.edges.length > 1 ? "boxes" : 'box'}
                              </span>
                            </div>

                            <div className="row g-3">
                              {order.lineItems?.edges?.map((edge, index) => {
                                const item = edge.node;
                                return (
                                  <div key={index} className="col-md-6">
                                    <div className="card border-0 bg-light h-100 product-card" style={{ borderRadius: '12px', transition: 'all 0.2s ease', boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px' }}>
                                      <div className="card-body p-3">
                                        <div className="row align-items-center">
                                          {item.product?.featuredImage?.url && (
                                            <div className="position-relative col-md-4">
                                              <img src={item.product.featuredImage.url} alt={item.title} className="rounded-3 img-fluid mb-4 mb-md-0" />
                                              <span className="position-absolute top-0 start-0 translate-middle badge rounded-pill bg-primary">
                                                {item.quantity}
                                              </span>
                                            </div>
                                          )}
                                          <div className="col-md-8">
                                            <h6 className="mb-2 fw-semibold text-truncate">{item.title}</h6>
                                            <div className="d-flex justify-content-between align-items-center">
                                              <span className="text-muted small">Unit Price</span>
                                              <span className="fw-bold text-success">
                                                ${(parseFloat(item.originalTotalSet?.shopMoney?.amount || 0) / item.quantity).toFixed(2)}
                                              </span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mt-1">
                                              <span className="text-muted small">Subtotal</span>
                                              <span className="fw-bold">
                                                ${parseFloat(item.originalTotalSet?.shopMoney?.amount || 0).toFixed(2)}
                                              </span>
                                            </div>
                                            {item.customAttributes?.length > 0 && (
                                              <div className="mt-3">
                                                <h6 className="mb-1 text-info fw-semibold">Cheerleader Details</h6>
                                                {item.customAttributes.map((attr, i) => (
                                                  <p key={i} className="mb-1 text-info small">
                                                    <strong>{attr.key.includes('Referral Code') ? 'Referral Code' : attr.key}:</strong>{' '}
                                                    {attr.value}
                                                  </p>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-5 pt-4 border-top">
                    <div className="text-muted">
                      Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredOrders.length)} of {filteredOrders.length} orders
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link border-0 rounded-pill me-1" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                            <ChevronLeft size={16} />
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                          <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                            <button className={`page-link border-0 rounded-pill mx-1 ${currentPage === i + 1 ? 'bg-primary' : 'bg-light text-dark'}`} onClick={() => setCurrentPage(i + 1)}>
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link border-0 rounded-pill ms-1" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                            <ChevronRight size={16} />
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals — Rendered at the end */}
      <TeamModal
        show={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        team={team}
        labels={labels}
        getTeamTypeDescription={getTeamTypeDescription}
      />

      <EditProfileModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        editForm={editForm}
        setEditForm={setEditForm}
        editLoading={editLoading}
        editMessage={editMessage}
        editMessageType={editMessageType}
        setEditMessage={setEditMessage}
        handleSaveProfile={handleSaveProfile}
        handleRegistrationTypeChange={handleRegistrationTypeChange}
        getNameLabel={getNameLabel}
        teamTypeOptions={teamTypeOptions}
        sizeOptions={sizeOptions}
        registrationTypes={registrationTypes}
      />

      <style jsx>{`
        .product-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
        }
        .custom-btn { color: white; border-color: white; }
      `}</style>
    </div>
  );
};

export default HomePage;